import { ResponsiveContainer, Treemap } from 'recharts';
import { CATEGORY_LABELS, CategoryKey, Nugget } from './types';
import { useEffect, useState } from 'react';
import JumbleModal from '../../components/jumbleModal/JumbleModal';
import {
  getDayDateTimeFromEntryDateTitle,
  getYearMonthDayNumbersFromCreationDateString,
  getDayMonthYearFromDate,
  dayMonthYearIsBetween
} from '../../util/dateTime';
import { loadExistingEntry } from '../../components/calendar/calendar';
import { EntryStates, isEntryStates } from '../../components/calendar/types';
import { Maximize2, Edit, ArrowLeft, Map as MapIcon } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const CustomizedContent = (props: {
  data: { name: string; size: number }[];
  handleClick: (name: string) => void;
  root?: any;
  depth?: any;
  x?: any;
  y?: any;
  width?: any;
  height?: any;
  index?: any;
  payload?: any;
  colors?: any;
  rank?: any;
  name?: any;
  fill?: any;
}) => {
  const { handleClick, fill, depth, x, y, width, height, name, data } = props;

  const MentorSelectorTooltip = <Tooltip id="mentor-selector-tooltip">{name}</Tooltip>;

  const size = data.find((d) => d.name === name)?.size;

  /*
   * Recharts asks the content renderer to draw the root node as well as the
   * leaves. The root covers the whole chart, so painting it filled the card
   * edge to edge in tile colour and every real tile read as a notch cut out
   * of one big block rather than as a tile of its own.
   */
  if (depth === 0) return <g />;

  // A 2px inset on each side is what separates neighbouring tiles. It replaces
  // a white background rect + a depth-scaled stroke, which drew #fff notches
  // at the rounded corners over a neutral-50 card.
  const inset = 2;
  const tileX = x + inset;
  const tileY = y + inset;
  const tileWidth = Math.max(0, width - inset * 2);
  const tileHeight = Math.max(0, height - inset * 2);

  return (
    <OverlayTrigger placement="auto" overlay={MentorSelectorTooltip}>
      <g>
        <rect
          x={tileX}
          y={tileY}
          width={tileWidth}
          height={tileHeight}
          rx={8}
          ry={8}
          style={{ fill, cursor: 'pointer' }}
          onClick={() => handleClick(name)} // Attach onClick event
        />
        {depth === 1 && width > name.length * 7 + 16 && height > 28 ? (
          <text
            className="tw-text-xs tw-font-normal"
            x={x + width / 2}
            y={y + height / 2 + 7}
            textAnchor="middle"
            fill="#fff"
            style={{ cursor: 'pointer' }}
            onClick={() => handleClick(name)}
          >
            {name}
          </text>
        ) : null}
        {/* number of entries with this tag, upper left hand corner */}
        {depth === 1 && width > 20 && height > 20 && size && size > 2 ? (
          <text className="tw-text-xs" x={x + 6} y={y + 20} style={{ fill: '#1e293b' }}>
            {size}
          </text>
        ) : null}
      </g>
    </OverlayTrigger>
  );
};

const TreeMapWrapper = (params: {
  data: { name: string; size: number }[];
  handleClick: (name: string) => void;
}) => {
  const { data, handleClick } = params;

  const COLORS = CATEGORY_LABELS.map((cat) => cat.color);

  return (
    <ResponsiveContainer width="100%" height={400}>
      <Treemap
        data={data}
        dataKey="size"
        nameKey="name"
        stroke="#fff"
        fill="#ac8ef1"
        content={
          <CustomizedContent data={data} colors={COLORS} handleClick={handleClick} />
        }
      ></Treemap>
    </ResponsiveContainer>
  );
};

const TreeMap = (props: {
  nuggets: Nugget[];
  loading: boolean;
  setShowPremiumModal: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { loading, nuggets } = props;

  const history = useHistory();

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState<JSX.Element>(<></>);
  const [entryIndexLoading, setEntryIndexLoading] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<Array<null | Date>>([
    new Date(new Date().setDate(new Date().getDate() - 14)),
    new Date()
  ]);
  const [startDate, endDate] = dateRange;

  const [tagMap, setTagMap] = useState<
    Map<string, { entryName: string; nuggetContent: string; category: CategoryKey }[]>
  >(new Map());

  const [treeMapData, setTreeMapData] = useState<{ name: string; size: number }[]>([]);

  useEffect(() => {
    const tempTagMap: Map<string, { entryName: string; nuggetContent: string; category: CategoryKey }[]> = new Map();

    let filteredNuggets = nuggets;
    if (startDate && endDate) {
      // Filter nuggets based on dateRange
      filteredNuggets = nuggets.filter((nugget) => {
        const nuggetDate = getYearMonthDayNumbersFromCreationDateString(nugget.entryName);
        const start = getDayMonthYearFromDate(startDate);
        const end = getDayMonthYearFromDate(endDate);

        return dayMonthYearIsBetween({ dayMonthYear: nuggetDate, start, end });
      });
    }

    filteredNuggets.forEach((nugget) => {
      nugget.tags.forEach((tag) => {
        if (!tempTagMap.has(tag)) {
          tempTagMap.set(tag, []);
        }
        tempTagMap
          .get(tag)!
          .push({ entryName: nugget.entryName, nuggetContent: nugget.content, category: nugget.category });
      });
    });
    setTagMap(tempTagMap);

    setTreeMapData(
      [...tempTagMap.entries()]
        .map(([tag, entries]) => ({
          name: tag,
          size: entries.length
        }))
        .sort((a, b) => b.size - a.size)
    );
  }, [nuggets, startDate, endDate]);

  const setModalContentToEntryContent = async (params: {
    nuggetName: string;
    day: string;
    date: string;
    time: string;
    entryName: string;
  }) => {
    const { day, date, time, entryName, nuggetName } = params;
    try {
      // handleClick && handleClick();
      const entryStates: EntryStates = [];
      const le = await loadExistingEntry({
        entryName: entryName
      });

      entryStates.push({
        ...le,
        name: entryName
      });
      if (!isEntryStates(entryStates)) {
        const error = `Invalid entry stats: ${JSON.stringify(entryStates)}`;
        console.error(error);
        throw new Error(error);
      }

      // set modal background color to nugget color
      const allSameEntry = entryStates.every((entryState) => entryState.name === entryStates[0].name);
      const dateString = entryStates.length === 1 || allSameEntry ? `${day}, ${date} at ${time}` : `${day}, ${date}`;
      setModalContent(
        <div className="tw-text-base tw-mb-4 tw-flex tw-flex-col tw-items-start tw-gap-3">
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
            <button
              onClick={() => setModalContentToTagNuggetContent(nuggetName)}
              className="tw-text-xs tw-text-neutral-600 tw-flex tw-p-1 tw-justify-center tw-border-none tw-bg-transparent"
            >
              <ArrowLeft className="tw-self-start" size={20} />
            </button>
            <div className="tw-text-xl tw-font-bold tw-self-center">{dateString}</div>
          </div>
          {entryStates.map((entryState) => {
            return (
              <div className="tw-inline">{`${entryState.blocks
                .filter((block) => block.role === 'user')
                .map((block) => `${block.content}\n\n`)}`}</div>
            );
          })}
          <button
            onClick={() => openEntryForEditing({ entryName, entryStates })}
            className="tw-text-xs tw-text-neutral-700 tw-flex tw-p-1 tw-justify-center tw-border-none tw-bg-transparent tw-self-end"
          >
            <Edit size={16} />
          </button>
        </div>
      );
    } catch (error) {
      alert('Error loading entry');
      console.error(error);
    }
  };

  const openEntryForEditing = async (params: { entryName: string; entryStates: EntryStates }) => {
    const { entryName, entryStates } = params;
    return history.push({
      pathname: `/entry/${entryName}`,
      state: {
        entryState: entryStates,
        returnPath: '/retrospective'
      }
    });
  };

  const setModalContentToTagNuggetContent = (name: string) => {
    const entryNames = tagMap.get(name)?.map((e) => e.entryName);
    if (!entryNames) {
      setModalContent(<div>name</div>);
    } else {
      setModalContent(
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-8">
          <div className="tw-text-sm tw-bg-gradient-to-b tw-from-primary-400 tw-to-secondary-500 tw-bg-clip-text tw-text-transparent tw-self-start tw-mb-4">
            {name}
          </div>
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-4">
            {tagMap.get(name)?.map((tagMetadata, index) => {
              const { day, date, time } = getDayDateTimeFromEntryDateTitle(tagMetadata.entryName);
              const year = tagMetadata.entryName.slice(0, 4);
              return (
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                  <div className="tw-text-base tw-font-bold tw-text-primary-900">
                    {tagMetadata.category} - {date}, {year}
                  </div>

                  <div key={`${JSON.stringify(tagMetadata)}`} className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                    <div className="tw-text-base tw-text-primary-900">{tagMetadata.nuggetContent} </div>
                    <button
                      disabled={entryIndexLoading !== null}
                      className={`tw-text-xs tw-flex tw-p-1 tw-justify-center tw-border-none tw-bg-transparent ${
                        index === entryIndexLoading && 'tw-animate-pulse'
                      }`}
                      onClick={async () => {
                        setEntryIndexLoading(index);
                        const dateString = `${day}, ${date}`;
                        await setModalContent(
                          <div className="tw-text-base tw-mb-4 tw-flex tw-flex-col tw-items-start tw-gap-3">
                            <div className="tw-text-xl tw-font-bold tw-mb-4">{dateString}</div>
                            <span
                              className="tw-mr-8 spinner-grow spinner-grow-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          </div>
                        );
                        await setModalContentToEntryContent({
                          nuggetName: name,
                          day,
                          date,
                          time,
                          entryName: tagMetadata.entryName
                        });
                        setEntryIndexLoading(null);
                      }}
                    >
                      <Maximize2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    setShowModal(true);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-6 tw-rounded-3xl tw-border-2 tw-border-solid tw-border-neutral-100 tw-bg-white tw-w-full tw-p-5 lg:tw-p-6">
      <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-justify-between sm:tw-items-center">
        <h2 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-font-light tw-text-xl lg:tw-text-2xl tw-text-neutral-800">
          <MapIcon className="tw-w-6 tw-h-6 tw-shrink-0" />
          Appendix of Journaling Topics
        </h2>
        <div>
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update: Array<Date | null>) => {
              setDateRange(update);
            }}
            isClearable={true}
            placeholderText="filter by date range"
            className="tw-text-sm tw-border tw-border-neutral-300 tw-rounded-lg tw-px-3 tw-min-h-control hover:tw-cursor-pointer tw-w-full sm:tw-w-56"
            maxDate={new Date(new Date().setDate(new Date().getDate() + 1))}
            minDate={new Date('2022-01-01')}
          />
        </div>
      </div>
      {loading ? (
        <div className="tw-flex tw-justify-center tw-items-center">
          <span className="tw-h-20 tw-w-20 spinner-border" role="status" aria-hidden="true"></span>
        </div>
      ) : treeMapData.length > 0 ? (
        <div className="">
          <TreeMapWrapper
            data={treeMapData}
            handleClick={setModalContentToTagNuggetContent}
          />
        </div>
      ) : (
        <div className="tw-text-lg tw-text-neutral-400 tw-text-center tw-pt-8">
          No journal entries found for the selected date range.
        </div>
      )}
      {/* used to show the entry content if user clicks on tag category */}
      <JumbleModal showModal={showModal} closeModal={() => setShowModal(false)} modalSections={[() => modalContent]} />
    </div>
  );
};

export default TreeMap;
